import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { DateTimeTzSchema, GenericTimelyAccountId } from './models.generic';

extendZodWithOpenApi(z);

/** NB! Enums defined in database as `account_role` and are NOT auto synced */
export const AccountRoleSchema = z
  .enum(['owner', 'admin', 'manager', 'member', 'employee', 'team_lead'])
  .describe(`The TIC account roles, including coverage of Timely's roles`)
  .default('member');

export type AccountRole = z.infer<typeof AccountRoleSchema>;

/** NB! Enums defined in database as `subscription_status` and are NOT auto synced */
export const AccountSubscriptionStatusSchema = z
  .enum([
    'trialing',
    'active',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'past_due',
    'unpaid',
    /** NOT part of enum in db, but helpful to identity the lacking shared state of TIC<->Timely */
    'unknown',
  ])
  .describe('Status of subscription')
  .default('active');

export const AccountIdSchema = z
  .string()
  .uuid()
  .describe('The TIC `account.id`')
  .openapi({ example: '123e4567-e89b-12d3-a456-426614174000' });

export const AccountNameSchema = z
  .string()
  .min(1)
  .describe('The TIC account name')
  .openapi({ example: 'My Team' });

export const AccountSchema = z.object({
  account_id: AccountIdSchema,
  team_name: AccountNameSchema,
  icon_url: z.string().nullable().describe('The TIC account icon URL'),
  account_role: AccountRoleSchema,
  subscription_active: z
    .boolean()
    .describe('Is the TIC account subscription active')
    .openapi({ example: true }),
  subscription_status: AccountSubscriptionStatusSchema.nullable().describe(
    'The account subscription status, or null if no subscription'
  ),
  personal_account: z.boolean().describe('Is the TIC account a personal account'),
  team_account: z.boolean().describe('Is the TIC account a team account'),
  timely_account_id: GenericTimelyAccountId.nullable(),
  primary_owner_user_id: z.string().uuid().describe('The TIC account primary owner user ID'),
  synced_with_timely_at: DateTimeTzSchema.nullable().describe(
    'The datetime when the account was last synced with Timely via "auth" operations'
  ),
});

export type Account = z.infer<typeof AccountSchema>;

export const PutAccountSchema = AccountSchema.pick({
  team_name: true,
});

export type PutAccount = z.infer<typeof PutAccountSchema>;

export const PostAccountSchema = PutAccountSchema;
export type PostAccountSchema = PutAccount;
